import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import VisionerLogo, { VisionerLogoWhite } from "@/components/VisionerLogo";

const navLinks = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Services", href: "/services" },
  { name: "Projects", href: "/projects" },
  { name: "Clients", href: "/clients" },
  { name: "Contact", href: "/contact" },
];

const Navbar = ({ isHeroPage = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Determine if we should show light or dark navbar
  const showLightNav = isHeroPage && !isScrolled;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showLightNav
          ? "bg-slate-900/40 backdrop-blur-sm"
          : "bg-white shadow-lg border-b border-slate-200/50"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center"
            data-testid="logo-link"
          >
            {showLightNav ? (
              <VisionerLogoWhite size="small" />
            ) : (
              <VisionerLogo size="small" />
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`nav-link text-sm font-medium transition-colors font-manrope ${
                  showLightNav 
                    ? "text-white/90 hover:text-white" 
                    : "text-slate-700 hover:text-slate-900"
                } ${location.pathname === link.href ? "font-bold" : ""}`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
                {location.pathname === link.href && (
                  <span className="absolute bottom-0 left-0 w-full h-0.5 bg-orange-500" />
                )}
              </Link>
            ))}
            <Link
              to="/contact"
              className={`px-6 py-3 font-manrope font-semibold uppercase tracking-wider text-sm transition-all ${
                showLightNav
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-slate-900 text-white hover:bg-slate-800"
              }`}
              data-testid="nav-cta-button"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 transition-colors ${
              showLightNav ? "text-white hover:text-white/80" : "text-slate-700 hover:text-slate-900"
            }`}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-200 shadow-lg"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-sm font-manrope ${
                    location.pathname === link.href ? "bg-slate-100 font-semibold" : ""
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full bg-orange-500 text-white hover:bg-orange-600 px-6 py-3 font-manrope font-semibold uppercase tracking-wider text-sm mt-4 text-center"
                data-testid="mobile-nav-cta"
              >
                Get Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
